import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import quizService, { QuizConfig, QuizState, QuizResult } from '../services/quizService';
import scoringEngine, { QuizScore, QuestionScore } from '../services/scoringEngine';
import { Question, MatchingPair } from '../questionGenerator/types';
import logger from '../utils/logger';
import aiEvaluationService, { AIEvaluationRequest, AIEvaluationResponse, BatchEvaluationProgress } from '../services/aiEvaluationService';

// AI Evaluation state for short answer questions
export interface ShortAnswerEvaluationState {
  userAnswer: string;
  userSelfAssessment?: number; // 0-4 band selected by user
  aiEvaluation?: AIEvaluationResponse;
  loading: boolean;
  error?: string;
  progress?: number; // 0-100 for progress bar
}

// Map of question index to evaluation state
export type EvaluationStateMap = Map<number, ShortAnswerEvaluationState>;

interface QuizContextType {
  // State
  quizState: QuizState | null;
  quizResult: QuizResult | null;
  quizScore: QuizScore | null;
  loading: boolean;
  error: string | null;

  // AI Evaluation state
  evaluations: EvaluationStateMap;
  evaluationLimitsRemaining: number;
  batchEvaluationProgress?: BatchEvaluationProgress;

  // Quiz control methods
  startQuiz: (config: QuizConfig) => Promise<void>;
  submitQuiz: () => Promise<void>;
  selectAnswer: (answer: number | MatchingPair[] | string | string[] | null) => void;
  validateCurrentAnswer: () => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  goToQuestion: (index: number) => void;
  pauseQuiz: () => void;
  resumeQuiz: () => void;
  resetQuiz: () => void;

  // Utility methods
  getCurrentQuestion: () => Question | null;
  getCurrentAnswer: () => any;
  isQuestionAnswered: (index: number) => boolean;
  isQuestionValidated: (index: number) => boolean;
  getProgress: () => { answered: number; total: number; percentage: number; validated: number };
  getRunningScore: () => number;
  predictFinalScore: () => number;

  // Scoring
  calculateQuestionScore: (isCorrect: boolean, timeSpent: number) => QuestionScore;
  getCurrentStreak: () => number;
  getQuestionScores: () => QuestionScore[];
  scoreAllUnscoredQuestions: () => void;

  // AI Evaluation methods
  selfAssessAnswer: (questionIndex: number, band: number) => void;
  requestAIEvaluation: (questionIndex: number) => Promise<void>;
  evaluateAllShortAnswers: () => Promise<void>;
  getEvaluationState: (questionIndex: number) => ShortAnswerEvaluationState | undefined;
}

const QuizContext = createContext<QuizContextType | undefined>(undefined);

export const useQuiz = () => {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};

interface QuizProviderProps {
  children: React.ReactNode;
}

export const QuizProvider: React.FC<QuizProviderProps> = ({ children }) => {
  const [quizState, setQuizState] = useState<QuizState | null>(null);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [quizScore, setQuizScore] = useState<QuizScore | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI Evaluation state
  const [evaluations, setEvaluations] = useState<EvaluationStateMap>(new Map());
  const [evaluationLimitsRemaining, setEvaluationLimitsRemaining] = useState(10); // Max 10 per quiz
  const [batchEvaluationProgress, setBatchEvaluationProgress] = useState<BatchEvaluationProgress | undefined>();

  // Load scoring configuration on mount
  useEffect(() => {
    scoringEngine.loadConfig().catch(err => {
      logger.error('Failed to load scoring config', err);
    });
  }, []);

  // Subscribe to quizService state changes (especially timer updates)
  useEffect(() => {
    // Subscribe to quiz state updates
    const unsubscribe = quizService.subscribe((state) => {
      // Update React state with new quiz state (spread to trigger React update)
      setQuizState({ ...state });
    });

    // Cleanup: unsubscribe when component unmounts
    return unsubscribe;
  }, []); // Empty deps - subscribe once, never recreate

  const startQuiz = useCallback(async (config: QuizConfig) => {
    try {
      setLoading(true);
      setError(null);
      setQuizResult(null);
      setQuizScore(null);

      // Reset scoring engine and clear state
      scoringEngine.reset();
      setQuizState(null);
      setQuizResult(null);
      setQuizScore(null);

      // Reset AI evaluation state
      setEvaluations(new Map());
      setEvaluationLimitsRemaining(10); // Reset to 10 evaluations per quiz
      setBatchEvaluationProgress(undefined);

      // Load scoring config for the quiz mode
      await scoringEngine.loadConfig();

      // Start the quiz
      const state = await quizService.startQuiz(config);
      setQuizState(state);
    } catch (err: unknown) {
      logger.error('Failed to start quiz', err);
      const message = err instanceof Error ? err.message : 'Failed to start quiz';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitQuiz = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Submit quiz and get result
      const result = await quizService.submitQuiz();
      setQuizResult(result);

      // Calculate partially correct answers from questionResults
      let partiallyCorrectCount = 0;
      if (result.questionResults && Array.isArray(result.questionResults)) {
        partiallyCorrectCount = result.questionResults.filter((qr: { isCorrect: boolean; partialCorrectCount?: number; totalParts?: number }) =>
          !qr.isCorrect && (qr.partialCorrectCount ?? 0) > 0 && (qr.totalParts ?? 0) > 0
        ).length;
      }

      // Use backend-calculated score directly (don't recalculate)
      const score: QuizScore = {
        totalScore: result.points || 0,           // Total points earned
        maxPossibleScore: (result.totalQuestions || 0) * 100, // Approximate max score
        correctAnswers: result.score || 0,        // Number of correct answers
        partiallyCorrectAnswers: partiallyCorrectCount,
        totalQuestions: result.totalQuestions || 0,
        percentage: result.percentage || 0,
        timeSpent: result.timeSeconds || 0,
        streakCount: 0,  // Not calculated in exam mode
        longestStreak: 0, // Not calculated in exam mode
        averageTimePerQuestion: result.totalQuestions ? (result.timeSeconds || 0) / result.totalQuestions : 0,
        questionScores: [], // Not needed for results display
        achievements: [], // Already handled separately in newAchievements
        streakBonus: 0,  // Not used in exam mode
        perfectBonus: result.score === result.totalQuestions ? 1 : 0
      };
      setQuizScore(score);

      // Update state
      const updatedState = quizService.getState();
      setQuizState(updatedState);
    } catch (err: unknown) {
      logger.error('Failed to submit quiz', err);
      const message = err instanceof Error ? err.message : 'Failed to submit quiz';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const selectAnswer = useCallback((answer: number | MatchingPair[] | string | string[] | null) => {
    if (!quizState) return;

    const currentQuestion = quizState.questions[quizState.currentQuestionIndex];
    if (!currentQuestion) return;

    // Store the answer (as temporary, not yet validated)
    quizService.selectAnswer(quizState.currentQuestionIndex, answer);

    // Update local state
    const updatedState = quizService.getState();
    setQuizState(updatedState ? { ...updatedState } : null);
  }, [quizState]);

  const validateCurrentAnswer = useCallback(() => {
    if (!quizState) return;

    const currentQuestion = quizState.questions[quizState.currentQuestionIndex];
    if (!currentQuestion) return;

    // Check if this question is already validated
    const alreadyValidated = quizService.isQuestionValidated(quizState.currentQuestionIndex);
    if (alreadyValidated) {
      return;
    }

    // Validate the current answer (moves temporaryAnswer to validatedAnswers)
    const validated = quizService.validateCurrentAnswer();
    if (!validated) return;

    // Calculate score for this question
    const isCorrect = quizService.checkAnswer(currentQuestion, quizState.temporaryAnswer);
    const timeSpent = quizService.getTimeSpentOnQuestion(quizState.currentQuestionIndex);

    // Calculate partial correctness for multi-part questions
    const partialCorrectness = quizService.calculatePartialCorrectness(currentQuestion, quizState.temporaryAnswer);

    // Get band score for short-answer questions
    // Note: Use explicit null/undefined check since bandScore of 0 is valid
    const bandScore = currentQuestion.questionType === 'short-answer' &&
      currentQuestion.metadata?.bandScore !== undefined && currentQuestion.metadata?.bandScore !== null
      ? (currentQuestion.metadata.bandScore as number)
      : undefined;

    // Skip scoring for short-answer questions in practice mode - they'll be scored after AI evaluation
    const shouldSkipScoring = currentQuestion.questionType === 'short-answer' &&
                              quizState.config.mode === 'practice' &&
                              bandScore === undefined; // Only skip if no band score yet

    let questionScore;
    if (!shouldSkipScoring) {
      questionScore = scoringEngine.calculateQuestionScore(
        isCorrect,
        currentQuestion.difficulty || 'medium',
        timeSpent,
        currentQuestion.questionType,
        partialCorrectness.correct,
        partialCorrectness.total,
        false,
        quizState.config.mode,
        bandScore
      );

      logger.debug(`Question ${quizState.currentQuestionIndex + 1} validated`, { isCorrect, points: questionScore.totalPoints });
    } else {
      logger.debug(`Question ${quizState.currentQuestionIndex + 1} validated (scoring deferred for AI evaluation)`);
    }

    // Update local state
    const updatedState = quizService.getState();
    setQuizState(updatedState ? { ...updatedState } : null);
  }, [quizState]);

  const nextQuestion = useCallback(() => {
    if (!quizState) return;

    const currentQuestion = quizState.questions[quizState.currentQuestionIndex];
    if (!currentQuestion) return;

    // Check if this question was already validated BEFORE moving to next
    const wasAlreadyValidated = quizService.isQuestionValidated(quizState.currentQuestionIndex);

    // Calculate score for this question if it's being validated for the first time
    if (!wasAlreadyValidated && quizState.temporaryAnswer !== null && quizState.temporaryAnswer !== undefined) {
      // Check if the answer is correct
      const isCorrect = quizService.checkAnswer(currentQuestion, quizState.temporaryAnswer);

      // Calculate time spent on this question
      const timeSpent = quizService.getTimeSpentOnQuestion(quizState.currentQuestionIndex);

      // Calculate partial correctness for multi-part questions
      const partialCorrectness = quizService.calculatePartialCorrectness(currentQuestion, quizState.temporaryAnswer);

      // Get band score for short-answer questions
      // Note: Use explicit null/undefined check since bandScore of 0 is valid
      const bandScore = currentQuestion.questionType === 'short-answer' &&
        currentQuestion.metadata?.bandScore !== undefined && currentQuestion.metadata?.bandScore !== null
        ? (currentQuestion.metadata.bandScore as number)
        : undefined;

      // Calculate and add the score for this question
      const questionScore = scoringEngine.calculateQuestionScore(
        isCorrect,
        currentQuestion.difficulty || 'medium',
        timeSpent,
        currentQuestion.questionType,
        partialCorrectness.correct,
        partialCorrectness.total,
        false, // hintUsed
        quizState.config.mode,
        bandScore
      );

      logger.debug(`Question ${quizState.currentQuestionIndex + 1} scored`, { isCorrect, points: questionScore.totalPoints });
    }

    const moved = quizService.nextQuestion();
    if (moved) {
      const updatedState = quizService.getState();
      setQuizState(updatedState ? { ...updatedState } : null);
    }
  }, [quizState]);

  const previousQuestion = useCallback(() => {
    if (!quizState) return;

    const moved = quizService.previousQuestion();
    if (moved) {
      const updatedState = quizService.getState();
      setQuizState(updatedState ? { ...updatedState } : null);
    }
  }, [quizState]);

  const goToQuestion = useCallback((index: number) => {
    if (!quizState) return;

    quizService.goToQuestion(index);
    const updatedState = quizService.getState();
    setQuizState(updatedState ? { ...updatedState } : null);
  }, [quizState]);

  const pauseQuiz = useCallback(() => {
    quizService.pauseQuiz();
    const updatedState = quizService.getState();
    setQuizState(updatedState ? { ...updatedState } : null);
  }, []);

  const resumeQuiz = useCallback(() => {
    quizService.resumeQuiz();
    const updatedState = quizService.getState();
    setQuizState(updatedState ? { ...updatedState } : null);
  }, []);

  const resetQuiz = useCallback(() => {
    quizService.clearQuiz();
    scoringEngine.reset();
    setQuizState(null);
    setQuizResult(null);
    setQuizScore(null);
    setError(null);

    // Reset AI evaluation state
    setEvaluations(new Map());
    setEvaluationLimitsRemaining(10);
    setBatchEvaluationProgress(undefined);
  }, []);

  const getCurrentQuestion = useCallback(() => {
    return quizService.getCurrentQuestion();
  }, []);

  const getCurrentAnswer = useCallback(() => {
    return quizService.getCurrentAnswer();
  }, []);

  const isQuestionAnswered = useCallback((index: number) => {
    return quizService.isQuestionAnswered(index);
  }, []);

  const isQuestionValidated = useCallback((index: number) => {
    return quizService.isQuestionValidated(index);
  }, []);

  const getProgress = useCallback(() => {
    return quizService.getProgress();
  }, []);

  const calculateQuestionScore = useCallback((isCorrect: boolean, timeSpent: number): QuestionScore => {
    if (!quizState) {
      return {
        basePoints: 0,
        difficultyBonus: 0,
        timeBonus: 0,
        streakBonus: 0,
        penalty: 0,
        partialCredit: 0,
        totalPoints: 0,
        breakdown: []
      };
    }

    const currentQuestion = quizState.questions[quizState.currentQuestionIndex];
    const difficulty = currentQuestion?.difficulty || 'medium';
    const questionType = currentQuestion?.questionType || 'multiple-choice';
    const mode = quizState.config.mode;

    return scoringEngine.calculateQuestionScore(
      isCorrect,
      difficulty,
      timeSpent,
      questionType,
      undefined,
      undefined,
      false,
      mode
    );
  }, [quizState]);

  const getRunningScore = useCallback(() => {
    return scoringEngine.getRunningScore();
  }, []);

  const predictFinalScore = useCallback(() => {
    if (!quizState) return 0;

    const progress = quizService.getProgress();
    return scoringEngine.predictFinalScore(progress.answered, progress.total);
  }, [quizState]);

  const getCurrentStreak = useCallback(() => {
    return scoringEngine.getCurrentStreak();
  }, []);

  const getQuestionScores = useCallback(() => {
    return scoringEngine.getQuestionScores();
  }, []);

  // Score all questions that have answers but haven't been scored yet
  const scoreAllUnscoredQuestions = useCallback(() => {
    if (!quizState) return;

    const currentScores = scoringEngine.getQuestionScores();
    const scoredCount = currentScores.length;

    // Score each question that hasn't been scored yet
    quizState.questions.forEach((question, index) => {
      // Skip if already scored
      if (index < scoredCount) return;

      const userAnswer = quizState.selectedAnswers[index];

      // Skip if no answer
      if (userAnswer === null || userAnswer === undefined) {
        // Score as zero/unanswered
        scoringEngine.calculateQuestionScore(
          false,
          question.difficulty || 'medium',
          0,
          question.questionType,
          0,
          0,
          false,
          quizState.config.mode,
          undefined
        );
        return;
      }

      // Check if correct
      const isCorrect = quizService.checkAnswer(question, userAnswer);

      // Calculate time spent (estimate if not tracked)
      const timeSpent = quizService.getTimeSpentOnQuestion(index) || 0;

      // Calculate partial correctness for multi-part questions
      const partialCorrectness = quizService.calculatePartialCorrectness(question, userAnswer);

      // Get band score for short-answer questions
      const bandScore = question.questionType === 'short-answer' &&
        question.metadata?.bandScore !== undefined && question.metadata?.bandScore !== null
        ? (question.metadata.bandScore as number)
        : undefined;

      // Calculate and add the score
      scoringEngine.calculateQuestionScore(
        isCorrect,
        question.difficulty || 'medium',
        timeSpent,
        question.questionType,
        partialCorrectness.correct,
        partialCorrectness.total,
        false,
        quizState.config.mode,
        bandScore
      );

      logger.debug(`Question ${index + 1} scored (batch)`, { isCorrect });
    });
  }, [quizState]);

  // ============= AI Evaluation Methods =============

  const selfAssessAnswer = useCallback((questionIndex: number, band: number) => {
    if (!quizState) return;

    const question = quizState.questions[questionIndex];
    if (!question || question.questionType !== 'short-answer') return;

    // Get current answer
    const userAnswer = quizService.getCurrentAnswer();
    if (!userAnswer) return;

    // Update evaluation state with self-assessment
    setEvaluations(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(questionIndex);

      newMap.set(questionIndex, {
        userAnswer: String(userAnswer), // Cast to string for short-answer questions
        userSelfAssessment: band,
        aiEvaluation: existing?.aiEvaluation,
        loading: false,
        error: existing?.error,
        progress: existing?.progress
      });

      return newMap;
    });

    logger.info(`Self-assessment recorded for question ${questionIndex + 1}: Band ${band}`);
  }, [quizState]);

  const requestAIEvaluation = useCallback(async (questionIndex: number) => {
    if (!quizState) {
      logger.error('Cannot evaluate: No quiz state');
      return;
    }

    const question = quizState.questions[questionIndex];
    if (!question || question.questionType !== 'short-answer') {
      logger.error('Cannot evaluate: Not a short-answer question');
      return;
    }

    // Check evaluation limits
    if (evaluationLimitsRemaining <= 0) {
      logger.warn('Evaluation limit reached');
      setEvaluations(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(questionIndex);
        newMap.set(questionIndex, {
          ...existing!,
          error: 'Evaluation limit reached for this quiz session'
        });
        return newMap;
      });
      return;
    }

    // Get the user's answer
    const validatedAnswers = quizService.getState()?.validatedAnswers;
    const userAnswer = validatedAnswers?.[questionIndex] || quizService.getCurrentAnswer();

    if (!userAnswer || typeof userAnswer !== 'string') {
      logger.error('Cannot evaluate: No answer provided');
      return;
    }

    // Set loading state
    setEvaluations(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(questionIndex);
      newMap.set(questionIndex, {
        userAnswer,
        userSelfAssessment: existing?.userSelfAssessment,
        loading: true,
        progress: 0
      });
      return newMap;
    });

    try {
      // Build evaluation request
      const evaluationRequest: AIEvaluationRequest = {
        questionId: question.id || '',
        question: question.question || '',
        intent: question.intent || '',
        rubric: (question.rubric as any) || {
          band4: { definition: 'Excellent', examples: [] },
          band3: { definition: 'Good', examples: [] },
          band2: { definition: 'Partial', examples: [] },
          band1: { definition: 'Poor', examples: [] },
          band0: { definition: 'No attempt', examples: [] }
        },
        learnerAnswer: userAnswer,
        sourceContent: question.sourceContent || '',
        generatedByModel: question.generatedByModel || '',
        assessmentPromptId: question.assessmentPromptId || '',
        mode: quizState.config.mode as 'practice' | 'exam'
      };

      // Evaluate with progress tracking
      const result = await aiEvaluationService.evaluateSingleAnswer(
        evaluationRequest,
        (progress) => {
          setEvaluations(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(questionIndex);
            if (existing) {
              newMap.set(questionIndex, {
                ...existing,
                progress
              });
            }
            return newMap;
          });
        }
      );

      // Update with result
      setEvaluations(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(questionIndex);
        newMap.set(questionIndex, {
          userAnswer,
          userSelfAssessment: existing?.userSelfAssessment,
          aiEvaluation: result,
          loading: false,
          progress: 100
        });
        return newMap;
      });

      // Store band score and feedback in question metadata for scoring calculation
      quizService.setQuestionBandScore(questionIndex, result.score, result.feedback);

      // Calculate time spent on this question
      const questionStartTimes = quizState.questionStartTimes;
      let timeSpent = 0;
      if (questionIndex < questionStartTimes.length - 1) {
        // Time between this question and the next
        timeSpent = Math.floor((questionStartTimes[questionIndex + 1] - questionStartTimes[questionIndex]) / 1000);
      } else if (questionIndex === questionStartTimes.length - 1) {
        // Time for the current/last viewed question
        timeSpent = Math.floor((Date.now() - questionStartTimes[questionIndex]) / 1000);
      }

      // Calculate and record points based on AI band score
      const questionScore = scoringEngine.calculateQuestionScore(
        result.score === 4, // isCorrect: true only for Band 4
        question.difficulty || 'medium',
        timeSpent,
        question.questionType,
        undefined, // partialCorrectCount
        undefined, // totalParts
        false, // hintUsed
        quizState.config.mode,
        result.score // bandScore
      );

      logger.info(`Short-answer question ${questionIndex + 1} scored:`, {
        band: result.score,
        points: questionScore.totalPoints,
        runningTotal: scoringEngine.getRunningScore()
      });

      // Trigger a state update to reflect the new score
      const updatedState = quizService.getState();
      setQuizState(updatedState ? { ...updatedState } : null);

      // Decrement evaluation limits
      setEvaluationLimitsRemaining(prev => prev - 1);

      logger.info(`AI evaluation complete for question ${questionIndex + 1}:`, result);
    } catch (error) {
      logger.error('AI evaluation failed:', error);

      setEvaluations(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(questionIndex);
        newMap.set(questionIndex, {
          userAnswer,
          userSelfAssessment: existing?.userSelfAssessment,
          loading: false,
          error: error instanceof Error ? error.message : 'Evaluation failed'
        });
        return newMap;
      });
    }
  }, [quizState, evaluationLimitsRemaining]);

  const evaluateAllShortAnswers = useCallback(async () => {
    if (!quizState) {
      logger.error('Cannot evaluate: No quiz state');
      return;
    }

    // Find all short-answer questions with answers
    const shortAnswerQuestions = quizState.questions
      .map((question, index) => ({ question, index }))
      .filter(({ question, index }) => {
        if (question.questionType !== 'short-answer') return false;

        const answer = quizState.validatedAnswers[index];
        return answer && typeof answer === 'string' && answer.trim().length > 0;
      });

    if (shortAnswerQuestions.length === 0) {
      logger.info('No short-answer questions to evaluate');
      return;
    }

    logger.info(`Starting batch evaluation of ${shortAnswerQuestions.length} short-answer questions`);

    // Build evaluation requests
    const evaluationRequests: AIEvaluationRequest[] = shortAnswerQuestions.map(({ question, index }) => ({
      questionId: question.id || '',
      question: question.question || '',
      intent: question.intent || '',
      rubric: (question.rubric as any) || {
        band4: { definition: 'Excellent', examples: [] },
        band3: { definition: 'Good', examples: [] },
        band2: { definition: 'Partial', examples: [] },
        band1: { definition: 'Poor', examples: [] },
        band0: { definition: 'No attempt', examples: [] }
      },
      learnerAnswer: quizState.validatedAnswers[index] as string,
      sourceContent: question.sourceContent || '',
      generatedByModel: question.generatedByModel || '',
      assessmentPromptId: question.assessmentPromptId || ''
    }));

    // Set all questions to loading state
    setEvaluations(prev => {
      const newMap = new Map(prev);
      shortAnswerQuestions.forEach(({ index }) => {
        newMap.set(index, {
          userAnswer: quizState.validatedAnswers[index] as string,
          loading: true,
          progress: 0
        });
      });
      return newMap;
    });

    try {
      // Evaluate batch with progress tracking
      const results = await aiEvaluationService.evaluateBatch(
        evaluationRequests,
        (progress) => {
          setBatchEvaluationProgress(progress);

          // Update progress for individual questions proportionally
          setEvaluations(prev => {
            const newMap = new Map(prev);
            shortAnswerQuestions.forEach(({ index }) => {
              const existing = newMap.get(index);
              if (existing && existing.loading) {
                newMap.set(index, {
                  ...existing,
                  progress: progress.percentage
                });
              }
            });
            return newMap;
          });
        }
      );

      // Update all evaluations with results
      setEvaluations(prev => {
        const newMap = new Map(prev);
        results.forEach((result, i) => {
          const { index } = shortAnswerQuestions[i];
          newMap.set(index, {
            userAnswer: quizState.validatedAnswers[index] as string,
            aiEvaluation: result,
            loading: false,
            progress: 100
          });

          // Store band score and feedback in question metadata for scoring calculation
          quizService.setQuestionBandScore(index, result.score, result.feedback);
        });
        return newMap;
      });

      setBatchEvaluationProgress(undefined);

      logger.info(`Batch evaluation complete: ${results.length} questions evaluated`);
    } catch (error) {
      logger.error('Batch evaluation failed:', error);

      // Set error state for all questions
      setEvaluations(prev => {
        const newMap = new Map(prev);
        shortAnswerQuestions.forEach(({ index }) => {
          const existing = newMap.get(index);
          newMap.set(index, {
            userAnswer: quizState.validatedAnswers[index] as string,
            loading: false,
            error: error instanceof Error ? error.message : 'Batch evaluation failed'
          });
        });
        return newMap;
      });

      setBatchEvaluationProgress(undefined);
    }
  }, [quizState]);

  const getEvaluationState = useCallback((questionIndex: number) => {
    return evaluations.get(questionIndex);
  }, [evaluations]);

  const value: QuizContextType = {
    // State
    quizState,
    quizResult,
    quizScore,
    loading,
    error,

    // AI Evaluation state
    evaluations,
    evaluationLimitsRemaining,
    batchEvaluationProgress,

    // Quiz control methods
    startQuiz,
    submitQuiz,
    selectAnswer,
    validateCurrentAnswer,
    nextQuestion,
    previousQuestion,
    goToQuestion,
    pauseQuiz,
    resumeQuiz,
    resetQuiz,

    // Utility methods
    getCurrentQuestion,
    getCurrentAnswer,
    isQuestionAnswered,
    isQuestionValidated,
    getProgress,
    getRunningScore,
    predictFinalScore,

    // Scoring
    calculateQuestionScore,
    getCurrentStreak,
    getQuestionScores,
    scoreAllUnscoredQuestions,

    // AI Evaluation methods
    selfAssessAnswer,
    requestAIEvaluation,
    evaluateAllShortAnswers,
    getEvaluationState,
  };

  return (
    <QuizContext.Provider value={value}>
      {children}
    </QuizContext.Provider>
  );
};

export default QuizContext;